import { SELECTED_BOOKING_TTL_MS, StoredBookingSelection } from 'store/selected-booking/consts';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The booking a stored entry names, or `null` if it names none worth restoring.
 *
 * Everything is checked rather than trusted. The entry lives on the HOST's origin, where the host
 * and anything else it frames can reach it, and arrives here over a postMessage bridge -- so it is
 * input, not state this app wrote.
 */
export const readStoredSelection = (
  raw: string | null | undefined,
  hostBuildingUuid?: string | null,
  now = Date.now(),
): string | null => {
  if (!raw || !hostBuildingUuid) return null;

  try {
    const parsed: unknown = JSON.parse(raw);

    if (!isPlainObject(parsed)) return null;

    const { transactionUuid, hostBuildingUuid: storedBuildingUuid, savedAt } = parsed;

    if (typeof transactionUuid !== 'string' || transactionUuid.length === 0) return null;
    if (typeof savedAt !== 'number' || !Number.isFinite(savedAt)) return null;
    // A booking opened in one building is not the next building's to reopen.
    if (storedBuildingUuid !== hostBuildingUuid) return null;

    /*
     * Bounded at BOTH ends. An upper bound alone lets a timestamp AHEAD of the clock read as fresh
     * for as long as it stays ahead -- which a device whose clock moved backwards after the write
     * produces on its own, with nobody tampering.
     */
    const age = now - savedAt;
    if (age < 0 || age > SELECTED_BOOKING_TTL_MS) return null;

    return transactionUuid;
  } catch {
    // The host hands back whatever is under the key; anything unparseable simply names no booking.
    return null;
  }
};

/** The entry to hand the host for a given selection. An empty uuid is how "none" travels. */
export const buildStoredSelection = (
  transactionUuid: string | null,
  hostBuildingUuid: string,
  now = Date.now(),
): StoredBookingSelection => ({
  transactionUuid: transactionUuid ?? '',
  hostBuildingUuid,
  savedAt: now,
});
